import WebFont from 'webfontloader';
import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ElasticTheme as Theme } from '@dev/ee-components';
import { parse, stringify } from 'query-string';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { Router as RouterProvider } from 'react-router-dom';
import { History } from '@dev/history';
import { Helmet } from 'react-helmet';
import { forEach, isEmpty, omit } from 'lodash-es';
import { useBrowserFingerprint } from '@dev/ee-fingerprint';
import { boot } from '@shared-store/shared-store';
import TagManager from '@sooro-io/react-gtm-module';

import Auth from '@/services/Authentication';
import Dialog from '@/services/Dialog';
import Layout from '@/services/Layout';
import SimpleLoader from '@/services/PageLoader';
import Router from '@/services/Router';
import ScrollIntoView from '@/services/ScrollIntoView';
import { Styles } from '@/services/Styles';
import { getSessionStore, SessionStartAuthWithDisposableToken, SessionSuccessAuth } from '@/store/session/actions';
import { AccountGetCustomBrandingStart, AccountStartLoading, getAccountStore } from '@/store/account/actions';
import { LoadProductList } from '@/store/products/actions';
import { SetIsStandalone, SetRouterBasePath } from '@/store/config/actions';

import { ProductType } from '@dev/ee-api/ee-api-models';
import translations from '../i18n';
import rootSaga from './sagas';
import { OptionalIntercomProvider } from './services/IntercomProvider';
import { ToastsProvider } from './services/ToastsProvider';
import { useNotifier } from './services/hooks/useNotifier';
import { ApplicationState, reducers } from './store';
import { StorageKeysEnum } from './services/dictionaries/StorageKeys';
import { HomeOnboardingProvider } from './services/contexts/HomeScreenOnboarding';
import { useGTMLoginEvent } from './services/hooks/ga4/useGTMLogniEvent';

const styles = {
    body: {
        fontFamily: 'Mulish',
        fontSize: '16px',
        color: Theme.palette.primary.main,
        overflow: 'auto',
        padding: 0,
        margin: 0,
        width: 'auto',
        height: '100vh',
        background: '#fff',
        overflowY: 'overlay',
    },
};

const tagManagerArgs = {
    gtmId: GTMID,
    source: GTM_SERVER
};

//@ts-expect-error datalayer
if (DEVMODE === 'PRODUCTION' && !!GTMID && !window.dataLayer) {
    TagManager.initialize(tagManagerArgs);
}

interface ApplicationProps {
    routerBasename?: string;
    history?: History;
    isBootedAsChild?: boolean;
}

interface AppProps {
    routerBasename?: string;
}

const ApplicationCore = (): JSX.Element => {
    useNotifier();

    return (
        <ScrollIntoView>
            <HomeOnboardingProvider>
                <Layout>
                    <Router />
                    <Dialog />
                </Layout>
            </HomeOnboardingProvider>
        </ScrollIntoView>
    );
};

const ConnectedApp = (props: ApplicationProps): JSX.Element => {
    const dispatch = useDispatch();
    const { history, routerBasename } = props;
    const { authorized } = useSelector(getSessionStore);
    const account = useSelector(getAccountStore);
    let queryParams = parse(window.location.search, { decode: false });

    if (!props.isBootedAsChild) {
        WebFont.load({
            google: {
                families: ['Nunito:400,600,700', 'Mulish', 'sans-serif'],
            },
        });
    }

    useLayoutEffect(() => {
        dispatch(AccountGetCustomBrandingStart());

        forEach(Object.keys(styles.body), (key) => {
            document.body.style[key] = styles.body[key];
        });

        if (routerBasename) {
            dispatch(SetRouterBasePath(routerBasename));
        }

        dispatch(SetIsStandalone(true));

        if (queryParams.i) {
            const publicAccountId = queryParams.i as string;

            sessionStorage.setItem('subaccountemailtouse', publicAccountId);
            sessionStorage.setItem('parentName', 'Admin');

            queryParams = omit(queryParams, ['i']);

            if (isEmpty(queryParams)) {
                history.replace({ search: stringify(queryParams) });
                window.location.reload();
            }
        }

        if (queryParams.t) {
            const token = queryParams.t as string;
            dispatch(SessionStartAuthWithDisposableToken(token));
            queryParams = omit(queryParams, ['t']);

            if (!isEmpty(queryParams)) {
                history.replace({ search: stringify(queryParams) });
            }
        }

        if (!account.GeneralInfo && !account.accountLoading && Auth.isAuthenticated()) {
            dispatch(SessionSuccessAuth());
            dispatch(AccountStartLoading());
        }
    }, []);

    useEffect(() => {
        if (Auth.isAuthenticated()) {
            dispatch(LoadProductList());
            if (queryParams.t) {
                const parsedQueryParams = omit(queryParams, ['t']);
                if (!isEmpty(parsedQueryParams)) {
                    history.replace({ search: stringify(parsedQueryParams) });
                }
                history.replace({ pathname: window.location.pathname });
            }
        }
    }, [authorized]);

    useGTMLoginEvent(ProductType.Marketing);

    return (
        <ToastsProvider>
            <ApplicationCore />
        </ToastsProvider>
    );
};

const App = (props: AppProps): JSX.Element => {
    const { history: reduxHistory, store } = boot<ApplicationState>({
        basename: props.routerBasename,
        isStandAlone: true,
        rootSaga,
        reducers,
        gaKey: GAKEY,
        devMode: DEVMODE,
    });

    const fingerprint = useBrowserFingerprint();

    if (fingerprint) {
        localStorage.setItem(StorageKeysEnum.Fingerprint, fingerprint);
    }

    return (
        <ReduxProvider store={store}>
            <Suspense fallback={<SimpleLoader />}>
                <I18nextProvider i18n={translations}>
                    <OptionalIntercomProvider>
                        <Helmet>
                            <title>Email Marketing - Dashboard</title>
                        </Helmet>

                        <RouterProvider history={reduxHistory}>
                            <Styles standalone={true}>
                                <ConnectedApp routerBasename={props.routerBasename} history={reduxHistory} />
                            </Styles>
                        </RouterProvider>
                    </OptionalIntercomProvider>
                </I18nextProvider>
            </Suspense>
        </ReduxProvider>
    );
};

export default App;
